import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../utilitarios/cn'
import { Botao } from './botao'

const LARGURAS = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
} as const

export interface PropriedadesModal {
  aberto: boolean
  onFechar: () => void
  titulo: ReactNode
  descricao?: ReactNode
  children: ReactNode
  rodape?: ReactNode
  largura?: keyof typeof LARGURAS
  /** Impede fechar por Esc/clique fora — para operação em curso. */
  travado?: boolean
}

/**
 * Modal do design system — o `Novo`/`Editar` dos catálogos (TELA-12 a 15) e as
 * confirmações de transferência.
 *
 * Foco é movido para o painel na abertura e o Esc fecha: sem isso, quem navega
 * por teclado continua no conteúdo de trás, que ficou coberto.
 */
export function Modal({
  aberto,
  onFechar,
  titulo,
  descricao,
  children,
  rodape,
  largura = 'md',
  travado,
}: PropriedadesModal) {
  const painel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberto) return

    const anterior = document.activeElement as HTMLElement | null
    painel.current?.focus()

    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape' && !travado) onFechar()
    }
    document.addEventListener('keydown', aoTeclar)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', aoTeclar)
      document.body.style.overflow = ''
      anterior?.focus?.()
    }
  }, [aberto, onFechar, travado])

  if (!aberto) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-6">
      <div
        aria-hidden="true"
        onClick={travado ? undefined : onFechar}
        className="fixed inset-0 bg-grafite-950/50 backdrop-blur-[2px]"
      />
      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={cn(
          'relative z-10 flex w-full flex-col rounded-t-xl bg-superficie shadow-lg outline-none sm:rounded-xl',
          'max-h-[92vh]',
          LARGURAS[largura],
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-borda px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-conteudo">{titulo}</h2>
            {descricao ? (
              <p className="mt-1 text-sm text-conteudo-suave">{descricao}</p>
            ) : null}
          </div>
          <Botao
            variante="fantasma"
            tamanho="icone"
            onClick={onFechar}
            disabled={travado}
            aria-label="Fechar"
            className="-mr-2 -mt-1"
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor">
              <path d="M6.3 5a.9.9 0 0 0-1.3 1.3L8.7 10 5 13.7A.9.9 0 0 0 6.3 15L10 11.3 13.7 15a.9.9 0 0 0 1.3-1.3L11.3 10 15 6.3A.9.9 0 0 0 13.7 5L10 8.7 6.3 5Z" />
            </svg>
          </Botao>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {rodape ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-borda bg-superficie-2 px-5 py-3">
            {rodape}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

export interface PropriedadesConfirmacao {
  aberto: boolean
  onFechar: () => void
  onConfirmar: () => void
  titulo: string
  descricao?: ReactNode
  rotuloConfirmar?: string
  varianteConfirmar?: 'primario' | 'perigo'
  processando?: boolean
  children?: ReactNode
}

/** Confirmação de ação sensível (desativar usuário, reabrir lead, lote). */
export function Confirmacao({
  aberto,
  onFechar,
  onConfirmar,
  titulo,
  descricao,
  rotuloConfirmar = 'Confirmar',
  varianteConfirmar = 'primario',
  processando,
  children,
}: PropriedadesConfirmacao) {
  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo={titulo}
      largura="sm"
      travado={processando}
      rodape={
        <>
          <Botao variante="contorno" onClick={onFechar} disabled={processando}>
            Cancelar
          </Botao>
          <Botao variante={varianteConfirmar} onClick={onConfirmar} carregando={processando}>
            {rotuloConfirmar}
          </Botao>
        </>
      }
    >
      {descricao ? <div className="text-sm text-conteudo-suave">{descricao}</div> : null}
      {children ? <div className={cn(descricao && 'mt-4')}>{children}</div> : null}
    </Modal>
  )
}
